import { REACT_APP_API_URL_E_CONTRACT } from "constants/index";

import { useState } from "react";

import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import FormControlLabel from "components/FormControlLabel";
import { base, gray, primary } from "configs/colors";
import { useEContractControllerOcrId, useUserControllerMe } from "generated/ec";
import { ErrorCustom, useNotification } from "hooks/useNotification";
import { FileUploader } from "react-drag-drop-files";
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import { useParams } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";

import { EyeIcon, Trash03Icon, UploadCloud02Icon } from "../Icon";
import Typography from "../Typography";

interface Props {
  onChange: (...event: any[]) => void;
  value: Blob;
  disable: boolean | undefined;
  fileType: any;
  isFrontIdCard: boolean;
}

const Control = (props: Props) => {
  const { onChange, value, disable, fileType, isFrontIdCard } = props;
  const [fileSelect, setFileSelect] = useState<Blob>();
  const [query, setQuery] = useQueryParams({
    employeeId: StringParam,
  });
  const { employeeId } = query;
  const { id: eContractId } = useParams();
  const { data: dataUser } = useUserControllerMe({
    axios: { baseURL: REACT_APP_API_URL_E_CONTRACT },
  });

  // eslint-disable-next-line
  const { mutate, isLoading, error } = useEContractControllerOcrId({
    mutation: {
      onSuccess(data: any) {
        const dataResult: any = data?.data;
        onChange(dataResult);
      },
    },
    axios: {
      baseURL: REACT_APP_API_URL_E_CONTRACT,
    },
  });

  const toBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleUploadFile = async (event: File) => {
    const file = event || null;
    const fileBase64 = await toBase64(file);
    const base64 = (fileBase64 as string).split(",")[1];
    if (file) {
      // eslint-disable-next-line
      setFileSelect(file);
      mutate({
        data: {
          base64img: base64,
          isFront: isFrontIdCard,
          eContractId,
          ...(employeeId
            ? { employeeId }
            : { ...(dataUser?.data.id ? { userId: dataUser.data.id } : {}) }),
        },
      });
    }
  };

  useNotification(error?.response?.data as ErrorCustom);

  return (
    <Button
      component="label"
      variant="text"
      sx={{
        width: "100%",
        paddingX: "0px",
        paddingY: "0px",
        height: "208px",
      }}
      disabled={disable}
    >
      <Box
        display="inline-block"
        borderRadius="12px"
        border={value ? `1px solid ${gray[300]}` : `1px dashed ${gray[300]}`}
        textAlign="center"
        sx={{
          background: base.White,
          cursor: "pointer",
          "&:hover": {
            opacity: 0.7,
          },
          position: "absolute",
          width: "100%",
        }}
        height="208px"
      >
        <FileUploader
          handleChange={(file: File) => {
            handleUploadFile(file);
          }}
          name="file"
          disabled={value}
          types={fileType}
          sx={{ width: 300 }}
        >
          {
            // eslint-disable-next-line
            value && fileSelect ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "208px",
                  padding: "14px",
                  position: "relative",
                }}
              >
                <Box
                  component="img"
                  sx={{
                    height: "100%",
                  }}
                  alt="img"
                  src={URL.createObjectURL(fileSelect)}
                />
                <Box
                  sx={{
                    position: "absolute",
                    marginLeft: "auto",
                    marginRight: "auto",
                    left: 0,
                    right: 0,
                    opacity: 0,
                    height: "100%",
                    "&:hover": {
                      opacity: 0.5,
                      display: "block",
                      backgroundColor: "black",
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignContent="center"
                    sx={{
                      height: "100%",
                    }}
                  >
                    <Box flex={1} alignSelf="center">
                      <IconButton
                        onClick={() => {
                          window.open(URL.createObjectURL(fileSelect));
                        }}
                        // disabled={disable}
                      >
                        <EyeIcon
                          sx={{
                            color: "white",
                          }}
                        />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          onChange(null);
                          setFileSelect(undefined);
                        }}
                        disabled={disable}
                      >
                        <Trash03Icon
                          sx={{
                            color: "white",
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : isLoading ? (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                height="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Box
                  border={`1px solid ${gray[300]}`}
                  alignSelf="center"
                  padding={1}
                  height="34px"
                  borderRadius="8px"
                >
                  <UploadCloud02Icon
                    fontSize="medium"
                    sx={{ color: gray[600] }}
                  />
                </Box>
                <Typography type="TextSM" color={gray[600]} textAlign="center">
                  <span style={{ color: primary[700], fontWeight: 600 }}>
                    Click to upload
                  </span>
                  &nbsp;or drag and drop
                </Typography>
                <Typography type="TextXS" color={gray[600]} textAlign="center">
                  {
                    fileType?.join(", ") //eslint-disable-line
                  }
                </Typography>
              </Box>
            )
          }
        </FileUploader>
      </Box>
    </Button>
  );
};

const UploadFileIDCardHookForm = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  defaultValue,
  rules,
  shouldUnregister,
  fileType,
  isFrontIdCard,
  setPropsWithFormState,
  ...innerProps
}: any) => {
  const { control } = useFormContext<TFieldValues>();
  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
      control={control}
      render={(formState) => {
        const {
          field: { disabled, ref, onChange, value, ...innerField },
          formState: { errors },
        } = formState;
        const error = errors[name];
        const errorMsg = error?.message;
        const helperText =
          typeof errorMsg === "string" ? errorMsg : innerProps?.helperText;
        return (
          <FormControlLabel
            control={
              <Control
                onChange={onChange}
                value={value}
                disable={innerProps.disabled}
                fileType={fileType}
                isFrontIdCard={isFrontIdCard}
              />
            }
            error
            helperText={helperText}
            label={innerProps?.label}
            required={innerProps.required}
          />
        );
      }}
    />
  );
};

export default UploadFileIDCardHookForm;
